import { useState, useEffect } from 'react'
import styles from './Preferences.module.css'
import apiClient from '../utils/api'
import UpgradePromptModal from './modals/UpgradePromptModal'
import { isFeatureAvailable } from '../utils/featureGate'
import { normalizeTimezone } from '../utils/timezoneUtils'

export default function Preferences({ onClose, currentTheme, onThemeChange, user, workspaces, currentWorkspace, featureTiers, initialSection }) {
    const [activeSection, setActiveSection] = useState(initialSection || 'appearance')
    const [tokens, setTokens] = useState([])
    const [showCreateToken, setShowCreateToken] = useState(false)
    const [showTokenDisplay, setShowTokenDisplay] = useState(false)
    const [newToken, setNewToken] = useState(null)
    const [tokenName, setTokenName] = useState('')
    const [tokenExpiry, setTokenExpiry] = useState('30')
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(currentWorkspace?.id || '')
    const [isLoading, setIsLoading] = useState(false)

    // Upgrade modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [upgradeModalData, setUpgradeModalData] = useState(null)

    // Google Calendar integration state
    const [calendarConnection, setCalendarConnection] = useState(null)
    const [availableCalendars, setAvailableCalendars] = useState([])
    const [selectedCalendars, setSelectedCalendars] = useState([])
    const [isFetchingCalendars, setIsFetchingCalendars] = useState(false)
    const [isFetchingEvents, setIsFetchingEvents] = useState(false)

    // Recurring events state
    const [showRecurringEventsSection, setShowRecurringEventsSection] = useState(false)
    const [recurringUpdateFrequency, setRecurringUpdateFrequency] = useState('daily')
    const [eventsResponse, setEventsResponse] = useState({ all_recurring_events: [] })
    const [configuredPreferences, setConfiguredPreferences] = useState([])
    const [isFetchingRecurringEvents, setIsFetchingRecurringEvents] = useState(false)
    const [projects, setProjects] = useState([])
    const [eventSelections, setEventSelections] = useState({}) // { recurring_event_id: { projectId, taskId, tasks: [], enabled: false, frequency: 'daily', modified: false } }

    // Timezone state
    const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || 'UTC')
    const [isSavingTimezone, setIsSavingTimezone] = useState(false)
    const [timezoneMessage, setTimezoneMessage] = useState(null)

    // Sync selectedTimezone with user prop changes
    useEffect(() => {
        if (user?.timezone && user.timezone !== selectedTimezone) {
            setSelectedTimezone(user.timezone)
        }
    }, [user?.timezone])


    const themes = [
        {
            id: 'light',
            name: 'Light Mode',
            description: 'Clean and bright interface with excellent readability',
            icon: 'light',
            colors: ['#ea580c', '#ffffff']
        },
        {
            id: 'dark',
            name: 'Dark Mode',
            description: 'Dark theme perfect for low-light environments',
            icon: 'dark',
            colors: ['#1f2937', '#374151']
        },
        {
            id: 'nightowl',
            name: 'Night Owl',
            description: 'Inspired by Sarah Drasner\'s Night Owl theme with purple accents',
            icon: 'nightowl',
            colors: ['#011627', '#c792ea']
        },
        {
            id: 'pastel',
            name: 'Pastel',
            description: 'Soft and dreamy theme with gentle pastel colors',
            icon: 'pastel',
            colors: ['#ff9a9e', '#a8e6cf']
        },
        {
            id: 'gamer',
            name: 'Gamer',
            description: 'High-contrast neon theme with gaming aesthetics and glow effects',
            icon: 'gamer',
            colors: ['#00ff88', '#ff0080']
        }
    ]

    const handleThemeSelect = (themeId) => {
        onThemeChange(themeId)
    }

    const handleTimezoneChange = (event) => {
        setSelectedTimezone(event.target.value)
        setTimezoneMessage(null)
    }

    const handleSaveTimezone = async () => {
        if (selectedTimezone === user?.timezone) {
            setTimezoneMessage({ type: 'info', text: 'Timezone is already set to this value' })
            return
        }

        setIsSavingTimezone(true)
        setTimezoneMessage(null)

        try {
            // Normalize timezone to handle aliases (e.g., Asia/Calcutta -> Asia/Kolkata)
            const normalizedTimezone = normalizeTimezone(selectedTimezone)
            await apiClient.updateTimezone(normalizedTimezone, true)
            setTimezoneMessage({ type: 'success', text: 'Timezone updated successfully!' })

            // Reload to get updated user data
            setTimeout(() => {
                window.location.reload()
            }, 1000)
        } catch (error) {
            console.error('Failed to update timezone:', error)
            setTimezoneMessage({ type: 'error', text: error.message || 'Failed to update timezone' })
        } finally {
            setIsSavingTimezone(false)
        }
    }

    // Common timezones grouped by region
    const timezoneOptions = [
        { label: 'UTC', value: 'UTC' },
        { label: '--- Americas ---', disabled: true },
        { label: 'Pacific Time (Los Angeles)', value: 'America/Los_Angeles' },
        { label: 'Mountain Time (Denver)', value: 'America/Denver' },
        { label: 'Central Time (Chicago)', value: 'America/Chicago' },
        { label: 'Eastern Time (New York)', value: 'America/New_York' },
        { label: 'São Paulo', value: 'America/Sao_Paulo' },
        { label: 'Buenos Aires', value: 'America/Argentina/Buenos_Aires' },
        { label: '--- Europe ---', disabled: true },
        { label: 'London', value: 'Europe/London' },
        { label: 'Paris', value: 'Europe/Paris' },
        { label: 'Berlin', value: 'Europe/Berlin' },
        { label: 'Rome', value: 'Europe/Rome' },
        { label: 'Moscow', value: 'Europe/Moscow' },
        { label: '--- Asia ---', disabled: true },
        { label: 'Dubai', value: 'Asia/Dubai' },
        { label: 'Kolkata', value: 'Asia/Kolkata' },
        { label: 'Bangkok', value: 'Asia/Bangkok' },
        { label: 'Singapore', value: 'Asia/Singapore' },
        { label: 'Hong Kong', value: 'Asia/Hong_Kong' },
        { label: 'Tokyo', value: 'Asia/Tokyo' },
        { label: 'Seoul', value: 'Asia/Seoul' },
        { label: '--- Pacific ---', disabled: true },
        { label: 'Sydney', value: 'Australia/Sydney' },
        { label: 'Melbourne', value: 'Australia/Melbourne' },
        { label: 'Auckland', value: 'Pacific/Auckland' },
    ]

    // Fetch tokens when switching to tokens section
    useEffect(() => {
        if (activeSection === 'tokens') {
            fetchTokens()
        }
    }, [activeSection])

    // Fetch calendar connection status when switching to integrations section
    useEffect(() => {
        if (activeSection === 'integrations') {
            fetchCalendarConnectionStatus()
        }
    }, [activeSection])

    // Check for calendar connected query param on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        if (urlParams.get('calendar_connected') === 'true') {
            setActiveSection('integrations')
            fetchCalendarConnectionStatus()
            // Remove query param
            window.history.replaceState({}, '', window.location.pathname)
        }
    }, [])

    // Sync selectedWorkspaceId with currentWorkspace to prevent controlled/uncontrolled input warning
    useEffect(() => {
        if (currentWorkspace?.id && selectedWorkspaceId !== currentWorkspace.id) {
            setSelectedWorkspaceId(currentWorkspace.id)
        }
    }, [currentWorkspace?.id])

    // Helper to handle feature-blocked errors
    const handleFeatureBlocked = (err) => {
        if (err.response?.status === 403 && err.response?.data) {
            setUpgradeModalData(err.response.data)
            setShowUpgradeModal(true)
            return true
        }
        return false
    }

    const fetchTokens = async () => {
        try {
            const response = await apiClient.get('/mcp_tokens')
            setTokens(response.tokens || [])
        } catch (error) {
            console.error('Failed to fetch tokens:', error)
        }
    }

    const handleCreateToken = async () => {
        if (!tokenName.trim() || !selectedWorkspaceId) return

        setIsLoading(true)
        try {
            const response = await apiClient.post('/mcp_tokens', {
                token: {
                    label: tokenName,
                    duration_minutes: parseInt(tokenExpiry) * 24 * 60,
                    workspace_id: selectedWorkspaceId
                }
            })

            setNewToken(response)
            setShowCreateToken(false)
            setShowTokenDisplay(true)
            setTokenName('')
            setSelectedWorkspaceId(currentWorkspace?.id || '')
            fetchTokens()
        } catch (error) {
            console.error('Failed to create token:', error)
            const errorMessage = error.response?.data?.error || 'Failed to create token. Please try again.'
            alert(errorMessage)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteToken = async (tokenId, isActive) => {
        const action = isActive ? 'revoke' : 'delete'
        const message = isActive
            ? 'Are you sure you want to revoke this token? It will stop working immediately.'
            : 'Are you sure you want to permanently delete this token?'

        if (!confirm(message)) return

        try {
            await apiClient.delete(`/mcp_tokens/${tokenId}`)
            fetchTokens()
        } catch (error) {
            console.error(`Failed to ${action} token:`, error)
            alert(`Failed to ${action} token. Please try again.`)
        }
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
        alert('Token copied to clipboard!')
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never'
        const date = new Date(dateString)
        const now = new Date()
        const diff = Math.abs(now - date)
        const days = Math.floor(diff / (1000 * 60 * 60 * 24))

        if (days === 0) return 'Today'
        if (days === 1) return 'Yesterday'
        if (days < 7) return `${days} days ago`
        return date.toLocaleDateString()
    }

    const formatExpiry = (expiresAt) => {
        const expiry = new Date(expiresAt)
        const now = new Date()

        if (expiry < now) return { text: 'Expired', isExpired: true }

        const diff = expiry - now
        const days = Math.floor(diff / (1000 * 60 * 60 * 24))

        if (days === 0) return { text: 'Expires today', isExpired: false }
        if (days === 1) return { text: 'Expires tomorrow', isExpired: false }
        if (days < 30) return { text: `Expires in ${days} days`, isExpired: false }
        return { text: `Expires ${expiry.toLocaleDateString()}`, isExpired: false }
    }

    // Google Calendar Integration Functions
    const fetchCalendarConnectionStatus = async () => {
        try {
            const response = await apiClient.get('/google_calendar/connection_status')
            setCalendarConnection(response)

            if (response.connected) {
                // Fetch available calendars if connected
                fetchAvailableCalendars()
                // Fetch recurring event preferences to determine Add/Modify button state
                try {
                    const preferencesResponse = await apiClient.get('/google_calendar/recurring_event_preferences')
                    setConfiguredPreferences(preferencesResponse.preferences || [])
                } catch (error) {
                    console.error('Failed to fetch recurring event preferences:', error)
                }
            }
        } catch (error) {
            console.error('Failed to fetch calendar connection status:', error)
            handleFeatureBlocked(error)
        }
    }

    const fetchAvailableCalendars = async () => {
        setIsFetchingCalendars(true)
        try {
            const response = await apiClient.get('/google_calendar/calendars')
            setAvailableCalendars(response.calendars || [])
            // Set selected calendars from backend (includes auto-selected ones)
            setSelectedCalendars(response.selected_calendars || [])
        } catch (error) {
            console.error('Failed to fetch calendars:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to fetch calendars. Please try reconnecting.')
            }
        } finally {
            setIsFetchingCalendars(false)
        }
    }

    const handleConnectGoogleCalendar = async () => {
        try {
            const response = await apiClient.get('/google_calendar/auth_url')
            // Redirect to Google OAuth
            window.location.href = response.auth_url
        } catch (error) {
            console.error('Failed to get auth URL:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to connect to Google Calendar. Please try again.')
            }
        }
    }

    const handleDisconnectGoogleCalendar = async () => {
        if (!confirm('Are you sure you want to disconnect Google Calendar? All synced events will be deleted.')) {
            return
        }

        try {
            await apiClient.delete('/google_calendar/disconnect')
            setCalendarConnection(null)
            setAvailableCalendars([])
            setSelectedCalendars([])
            setConfiguredPreferences([])

            // Dispatch custom event to notify Calendar component
            window.dispatchEvent(new CustomEvent('googleCalendarDisconnected'))

            alert('Google Calendar disconnected successfully')
        } catch (error) {
            console.error('Failed to disconnect:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to disconnect Google Calendar. Please try again.')
            }
        }
    }

    const handleToggleCalendar = (calendarId) => {
        const newSelectedCalendars = selectedCalendars.some(cal => cal.id === calendarId)
            ? selectedCalendars.filter(cal => cal.id !== calendarId)
            : [...selectedCalendars, availableCalendars.find(cal => cal.id === calendarId)]

        setSelectedCalendars(newSelectedCalendars)
        // Automatically save the selection
        apiClient.post('/google_calendar/update_selected_calendars', {
            selected_calendars: newSelectedCalendars
        }).then(() => {
            // Dispatch custom event to trigger calendar refresh
            window.dispatchEvent(new CustomEvent('calendarSelectionChanged'))
        }).catch(error => {
            console.error('Failed to save calendar selection:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to save calendar selection. Please try again.')
            }
        })
    }

    // Recurring Events Functions
    const fetchRecurringEvents = async () => {
        setIsFetchingRecurringEvents(true)
        try {
            const [recurringEventsResp, preferencesResponse, projectsResponse] = await Promise.all([
                apiClient.get('/google_calendar/recurring_events'),
                apiClient.get('/google_calendar/recurring_event_preferences'),
                apiClient.get('/projects')
            ])

            setEventsResponse(recurringEventsResp)
            setConfiguredPreferences(preferencesResponse.preferences || [])
            setProjects(projectsResponse.projects || [])

            // Initialize event selections from existing preferences
            const selections = {}
            for (const pref of preferencesResponse.preferences) {
                const tasks = []
                if (pref.project.id) {
                    try {
                        const tasksResponse = await apiClient.get(`/projects/${pref.project.id}/tasks`)
                        tasks.push(...(tasksResponse.tasks || []))
                    } catch (error) {
                        console.error('Failed to fetch tasks for project:', error)
                    }
                }

                selections[pref.recurring_event_id] = {
                    projectId: pref.project.id,
                    taskId: pref.task?.id || '',
                    tasks: tasks,
                    enabled: pref.enabled !== undefined ? pref.enabled : true,
                    frequency: pref.update_frequency || 'daily',
                    modified: false
                }
            }
            setEventSelections(selections)
        } catch (error) {
            console.error('Failed to fetch recurring events:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to load recurring events. Please try again.')
            }
        } finally {
            setIsFetchingRecurringEvents(false)
        }
    }

    const handleToggleRecurringEventsSection = async (enabled) => {
        setShowRecurringEventsSection(enabled)
        if (enabled) {
            await fetchRecurringEvents()
        }
    }

    const handleProjectChangeForEvent = async (eventId, projectId) => {
        const newSelections = { ...eventSelections }
        if (!newSelections[eventId]) {
            newSelections[eventId] = { projectId: '', taskId: '', tasks: [], enabled: false, frequency: 'daily', modified: false }
        }

        const wasEnabled = newSelections[eventId].enabled

        newSelections[eventId].projectId = projectId
        newSelections[eventId].taskId = ''
        newSelections[eventId].tasks = []

        if (projectId) {
            try {
                const response = await apiClient.get(`/projects/${projectId}/tasks`)
                newSelections[eventId].tasks = response.tasks || []
            } catch (error) {
                console.error('Failed to fetch tasks:', error)
            }
        }

        setEventSelections(newSelections)

        // If event was already enabled, auto-save the project change
        if (wasEnabled && projectId) {
            const event = eventsResponse.all_recurring_events.find(e => e.recurring_event_id === eventId)
            try {
                await apiClient.post('/google_calendar/recurring_event_preferences', {
                    recurring_event_id: eventId,
                    event_summary: event.summary,
                    calendar_id: event.calendar_id,
                    project_id: projectId,
                    task_id: null,
                    update_frequency: newSelections[eventId].frequency || 'daily',
                    enabled: true
                })
                await fetchRecurringEvents()
            } catch (error) {
                console.error('Failed to save preference:', error)
                if (!handleFeatureBlocked(error)) {
                    alert('Failed to save preference. Please try again.')
                }
            }
        }
    }

    const handleTaskChangeForEvent = async (eventId, taskId) => {
        const newSelections = { ...eventSelections }
        if (!newSelections[eventId]) {
            newSelections[eventId] = { projectId: '', taskId: '', tasks: [], enabled: false, frequency: 'daily', modified: false }
        }

        const wasEnabled = newSelections[eventId].enabled

        newSelections[eventId].taskId = taskId
        setEventSelections(newSelections)

        // If event was already enabled, auto-save the task change
        if (wasEnabled && newSelections[eventId].projectId) {
            const event = eventsResponse.all_recurring_events.find(e => e.recurring_event_id === eventId)
            try {
                await apiClient.post('/google_calendar/recurring_event_preferences', {
                    recurring_event_id: eventId,
                    event_summary: event.summary,
                    calendar_id: event.calendar_id,
                    project_id: newSelections[eventId].projectId,
                    task_id: taskId || null,
                    update_frequency: newSelections[eventId].frequency || 'daily',
                    enabled: true
                })
                await fetchRecurringEvents()
            } catch (error) {
                console.error('Failed to save preference:', error)
                if (!handleFeatureBlocked(error)) {
                    alert('Failed to save preference. Please try again.')
                }
            }
        }
    }

    const handleToggleEventEnabled = async (eventId, enabled) => {
        const newSelections = { ...eventSelections }
        if (!newSelections[eventId]) {
            newSelections[eventId] = { projectId: '', taskId: '', tasks: [], enabled: false, frequency: 'daily', modified: false }
        }

        // Validate project selection when enabling
        if (enabled && !newSelections[eventId].projectId) {
            alert('Please select a project before enabling updates for this event.')
            return
        }

        newSelections[eventId].enabled = enabled
        // When enabling, default frequency is 'daily'
        if (enabled && !newSelections[eventId].frequency) {
            newSelections[eventId].frequency = 'daily'
        }

        setEventSelections(newSelections)

        // Immediately save the preference when toggling
        const event = eventsResponse.all_recurring_events.find(e => e.recurring_event_id === eventId)

        try {
            if (enabled) {
                // Save/create preference when enabling
                await apiClient.post('/google_calendar/recurring_event_preferences', {
                    recurring_event_id: eventId,
                    event_summary: event.summary,
                    calendar_id: event.calendar_id,
                    project_id: newSelections[eventId].projectId,
                    task_id: newSelections[eventId].taskId || null,
                    update_frequency: newSelections[eventId].frequency || 'daily',
                    enabled: true
                })

                // Refresh the preferences list
                await fetchRecurringEvents()
                alert('Event enabled successfully! It will now update daily.')
            } else {
                // Disable preference when turning off
                const existingPref = configuredPreferences.find(
                    p => p.recurring_event_id === eventId
                )
                if (existingPref) {
                    await apiClient.delete(`/google_calendar/recurring_event_preferences/${existingPref.id}`, {
                        params: { frequency: existingPref.update_frequency }
                    })

                    // Refresh the preferences list
                    await fetchRecurringEvents()
                    alert('Event disabled successfully.')
                }
            }
        } catch (error) {
            console.error('Failed to save preference:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to save preference. Please try again.')
            }
            // Revert the toggle on error
            newSelections[eventId].enabled = !enabled
            setEventSelections(newSelections)
        }
    }

    const handleFrequencyChangeForEvent = (eventId, frequency) => {
        const newSelections = { ...eventSelections }
        if (!newSelections[eventId]) {
            newSelections[eventId] = { projectId: '', taskId: '', tasks: [], enabled: false, frequency: 'daily', modified: false }
        }
        newSelections[eventId].frequency = frequency
        newSelections[eventId].modified = true
        setEventSelections(newSelections)
    }

    const getProjectForEvent = (eventId) => {
        return eventSelections[eventId]?.projectId || ''
    }

    const getTasksForEvent = (eventId) => {
        return eventSelections[eventId]?.tasks || []
    }

    const handleUpdateAllWorklogs = async () => {
        // Filter events that have been modified
        const modifiedEventsToSave = Object.entries(eventSelections)
            .filter(([_, selection]) => selection.modified && selection.projectId)
            .map(([eventId]) => eventId)

        const modifiedEventsToDelete = Object.entries(eventSelections)
            .filter(([eventId, selection]) => {
                // Delete if modified to empty projectId AND has an existing preference
                const existingPref = configuredPreferences.find(
                    p => p.recurring_event_id === eventId
                )
                return selection.modified && !selection.projectId && existingPref
            })
            .map(([eventId]) => eventId)

        if (modifiedEventsToSave.length === 0 && modifiedEventsToDelete.length === 0) {
            alert('No changes to save. Please modify at least one event.')
            return
        }

        try {
            const promises = []

            // Save/update preferences for events with a project selected
            modifiedEventsToSave.forEach(eventId => {
                const selection = eventSelections[eventId]
                const event = eventsResponse.all_recurring_events.find(e => e.recurring_event_id === eventId)

                promises.push(
                    apiClient.post('/google_calendar/recurring_event_preferences', {
                        recurring_event_id: eventId,
                        event_summary: event.summary,
                        calendar_id: event.calendar_id,
                        project_id: selection.projectId,
                        task_id: selection.taskId || null,
                        update_frequency: selection.frequency || recurringUpdateFrequency
                    })
                )
            })

            // Delete preferences for events where "Select Project" was chosen
            modifiedEventsToDelete.forEach(eventId => {
                const existingPref = configuredPreferences.find(
                    p => p.recurring_event_id === eventId
                )
                if (existingPref) {
                    const frequency = eventSelections[eventId]?.frequency || existingPref.update_frequency
                    promises.push(
                        apiClient.delete(`/google_calendar/recurring_event_preferences/${existingPref.id}`, {
                            params: { frequency: frequency }
                        })
                    )
                }
            })

            await Promise.all(promises)
            await fetchRecurringEvents()

            // Reset modified flags
            const resetSelections = { ...eventSelections }
            Object.keys(resetSelections).forEach(key => {
                if (resetSelections[key]) {
                    resetSelections[key].modified = false
                }
            })
            setEventSelections(resetSelections)

            const totalChanges = modifiedEventsToSave.length + modifiedEventsToDelete.length
            const savedMsg = modifiedEventsToSave.length > 0 ? `${modifiedEventsToSave.length} saved` : ''
            const deletedMsg = modifiedEventsToDelete.length > 0 ? `${modifiedEventsToDelete.length} modified` : ''
            const message = [savedMsg, deletedMsg].filter(Boolean).join(', ')

            alert(`Successfully updated recurring event preferences (${message})!`)
        } catch (error) {
            console.error('Failed to save preferences:', error)
            if (!handleFeatureBlocked(error)) {
                alert('Failed to save preferences. Please try again.')
            }
        }
    }

    const renderAppearanceSection = () => (
        <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
                <h3>Theme Selection</h3>
                <p>Choose your preferred color theme for the application</p>
            </div>

            <div className={styles.themeGrid}>
                {themes.map(theme => (
                    <div
                        key={theme.id}
                        className={`${styles.themeCard} ${currentTheme === theme.id ? styles.selected : ''}`}
                        onClick={() => handleThemeSelect(theme.id)}
                    >
                        <div className={styles.themeIconContainer}>
                            {theme.icon === 'light' ? (
                                <div className={styles.lightModeIcon}>
                                    <div className={styles.orangeSquare}></div>
                                    <div className={styles.whiteSquare}></div>
                                </div>
                            ) : theme.icon === 'dark' ? (
                                <div className={styles.darkModeIcon}>🌙</div>
                            ) : theme.icon === 'nightowl' ? (
                                <div className={styles.nightOwlIcon}>🦉</div>
                            ) : theme.icon === 'pastel' ? (
                                <div className={styles.pastelIcon}>🌅</div>
                            ) : theme.icon === 'gamer' ? (
                                <div className={styles.gamerIcon}>🌲</div>
                            ) : (
                                <div
                                    className={styles.themePreview}
                                    style={{
                                        background: `linear-gradient(135deg, ${theme.colors[0]} 0%, ${theme.colors[1]} 100%)`
                                    }}
                                ></div>
                            )}
                        </div>
                        <div className={styles.themeInfo}>
                            <h4>{theme.name}</h4>
                            <p>{theme.description}</p>
                        </div>
                        {currentTheme === theme.id && (
                            <div className={styles.selectedIndicator}>✓</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )

    const renderAccountSection = () => (
        <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
                <h3>Account Settings</h3>
                <p>Manage your account information and security settings</p>
            </div>

            <div className={styles.settingsGroup}>
                <div className={styles.settingItem}>
                    <label>Display Name</label>
                    <input
                        type="text"
                        value={`${user?.first_name || ''} ${user?.last_name || ''}`}
                        className={styles.settingInput}
                        readOnly
                    />
                </div>
                <div className={styles.settingItem}>
                    <label>Email Address</label>
                    <input
                        type="email"
                        value={user?.email || ''}
                        className={styles.settingInput}
                        readOnly
                    />
                </div>
                <div className={styles.settingItem}>
                    <label>Time Zone</label>
                    <p className={styles.settingDescription}>
                        Your timezone is used for accurate worklog tracking and midnight worklog splitting.
                        {user?.timezone_confirmed_at && (
                            <span className={styles.confirmedBadge}>
                                ✓ Confirmed {new Date(user.timezone_confirmed_at).toLocaleDateString()}
                            </span>
                        )}
                    </p>
                    <div className={styles.timezoneControl}>
                        <select
                            className={styles.settingSelect}
                            value={selectedTimezone}
                            onChange={handleTimezoneChange}
                            disabled={isSavingTimezone}
                        >
                            {timezoneOptions.map((option, index) => (
                                <option
                                    key={index}
                                    value={option.value}
                                    disabled={option.disabled}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            className={styles.saveTimezoneButton}
                            onClick={handleSaveTimezone}
                            disabled={isSavingTimezone || selectedTimezone === user?.timezone}
                        >
                            {isSavingTimezone ? 'Saving...' : 'Save Timezone'}
                        </button>
                    </div>
                    {timezoneMessage && (
                        <div className={`${styles.timezoneMessage} ${styles[timezoneMessage.type]}`}>
                            {timezoneMessage.text}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )

    const renderPersonalAccessTokensSection = () => (
        <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
                <h3>Personal Access Tokens</h3>
                <p>Manage API tokens across all your workspaces. Each token is scoped to a specific workspace and only has access to data within that workspace.</p>
            </div>

            <button
                className={styles.generateTokenButton}
                onClick={() => setShowCreateToken(true)}
            >
                + Generate New Token
            </button>

            {tokens.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No tokens yet</p>
                    <p className={styles.emptyStateSubtext}>Generate your first token to get started</p>
                </div>
            ) : (
                <div className={styles.tokenList}>
                    <table className={styles.tokenTable}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Workspace</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Last Used</th>
                                <th>Expires</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map(token => {
                                const expiry = formatExpiry(token.expires_at)
                                return (
                                    <tr key={token.id}>
                                        <td>{token.client_name}</td>
                                        <td className={styles.workspaceCell}>{token.workspace_name}</td>
                                        <td>
                                            <span className={`${styles.statusBadge} ${styles[`status_${token.status}`]}`}>
                                                {token.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(token.created_at)}</td>
                                        <td>{formatDate(token.last_used_at)}</td>
                                        <td className={expiry.isExpired ? styles.expiredText : ''}>
                                            {expiry.text}
                                        </td>
                                        <td>
                                            {token.status === 'active' ? (
                                                <button
                                                    className={styles.revokeButton}
                                                    onClick={() => handleDeleteToken(token.id, true)}
                                                >
                                                    Revoke
                                                </button>
                                            ) : (
                                                <button
                                                    className={styles.deleteButton}
                                                    onClick={() => handleDeleteToken(token.id, false)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <details className={styles.setupInstructions}>
                <summary>How to use with Claude Code MCP</summary>
                <div className={styles.instructionsContent}>
                    <p>For detailed setup instructions, visit:</p>
                    <a
                        href="https://beskar-kadlekai-mcp.s3.amazonaws.com/index.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.documentationLink}
                    >
                        https://beskar-kadlekai-mcp.s3.amazonaws.com/index.html
                    </a>
                </div>
            </details>

            {/* Create Token Modal */}
            {showCreateToken && (
                <div className={styles.modalOverlay} onClick={() => setShowCreateToken(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3>Generate New Token</h3>
                        <p className={styles.modalSubtext}>Choose a descriptive name, workspace, and expiration for your token</p>

                        <div className={styles.formGroup}>
                            <label>Token Name</label>
                            <input
                                type="text"
                                placeholder="e.g., Claude Code Local, Production API"
                                value={tokenName}
                                onChange={(e) => setTokenName(e.target.value)}
                                className={styles.input}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label>Workspace</label>
                            <select
                                value={selectedWorkspaceId}
                                onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                                className={styles.select}
                            >
                                {workspaces && workspaces.map(workspace => (
                                    <option key={workspace.id} value={workspace.id}>
                                        {workspace.name}
                                    </option>
                                ))}
                            </select>
                            <p className={styles.helpText}>
                                This token will only have access to data in the selected workspace
                            </p>
                        </div>

                        <div className={styles.formGroup}>
                            <label>Expiration</label>
                            <select
                                value={tokenExpiry}
                                onChange={(e) => setTokenExpiry(e.target.value)}
                                className={styles.select}
                            >
                                <option value="7">7 days</option>
                                <option value="30">30 days</option>
                                <option value="90">90 days</option>
                                <option value="365">1 year</option>
                            </select>
                        </div>

                        <div className={styles.modalActions}>
                            <button
                                className={styles.cancelButton}
                                onClick={() => setShowCreateToken(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.primaryButton}
                                onClick={handleCreateToken}
                                disabled={isLoading || !tokenName.trim() || !selectedWorkspaceId}
                            >
                                {isLoading ? 'Generating...' : 'Generate Token'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Token Display Modal */}
            {showTokenDisplay && newToken && (
                <div className={styles.modalOverlay} onClick={() => setShowTokenDisplay(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <h3>⚠️ Save Your Token</h3>
                        <p className={styles.warningText}>
                            Make sure to copy your token now. You won't be able to see it again!
                        </p>

                        <div className={styles.tokenDisplay}>
                            <code>{newToken.token}</code>
                            <button
                                className={styles.copyButton}
                                onClick={() => copyToClipboard(newToken.token)}
                            >
                                Copy
                            </button>
                        </div>

                        <div className={styles.tokenInfo}>
                            <p><strong>Name:</strong> {newToken.client_name}</p>
                            <p><strong>Expires:</strong> {new Date(newToken.expires_at).toLocaleString()}</p>
                        </div>

                        <button
                            className={styles.primaryButton}
                            onClick={() => setShowTokenDisplay(false)}
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </div>
    )

    const renderIntegrationsSection = () => {
        // Check if Google Calendar integration is available for this workspace plan.
        // Uses featureTiers prop passed from Home (avoids React hook issues with usePage).
        const plan = currentWorkspace?.plan?.toLowerCase() || 'free'
        const canAccessGoogleCalendar = featureTiers?.[plan]?.features?.google_calendar_integration
            ?? featureTiers?.free?.features?.google_calendar_integration
            ?? true // default to available if tiers data is missing

        return (
            <div className={styles.sectionContent}>
                <div className={styles.sectionHeader}>
                    <h3>Integrations</h3>
                    <p>Connect external services to automatically sync data with Kadlekai</p>
                </div>

                {/* Google Calendar Integration */}
                {canAccessGoogleCalendar ? (
                    <div className={styles.integrationCard}>
                <div className={styles.integrationHeader}>
                    <div className={styles.integrationInfo}>
                        <div className={styles.integrationIcon}>
                            <img
                                src="/google-calendar-logo.png"
                                alt="Google Calendar"
                                width="48"
                                height="48"
                            />
                        </div>
                        <div>
                            <h4>Google Calendar</h4>
                            <p>Connect Kadlekai to your Google account to sync calendar events</p>
                        </div>
                    </div>
                    {!calendarConnection?.connected ? (
                        <button
                            className={styles.connectButton}
                            onClick={handleConnectGoogleCalendar}
                        >
                            CONNECT
                        </button>
                    ) : (
                        <div className={styles.integrationActions}>
                            <span className={styles.connectedBadge}>✓ Connected</span>
                            <button
                                className={styles.disconnectButton}
                                onClick={handleDisconnectGoogleCalendar}
                            >
                                Disconnect
                            </button>
                        </div>
                    )}
                </div>

                {calendarConnection?.connected && (
                    <div className={styles.integrationDetails}>
                        {/* Connection Status */}
                        {calendarConnection.last_error && (
                            <div className={styles.errorBanner}>
                                <span>⚠️ {calendarConnection.last_error}</span>
                                <button onClick={handleConnectGoogleCalendar}>Reconnect</button>
                            </div>
                        )}

                        {/* Last Fetch Status */}
                        {calendarConnection.last_fetch_at && (
                            <div className={styles.fetchStatus}>
                                <span>Last synced: {formatDate(calendarConnection.last_fetch_at)}</span>
                                {calendarConnection.last_fetch_status === 'success' && (
                                    <span className={styles.statusSuccess}>✓ Success</span>
                                )}
                                {calendarConnection.last_fetch_status === 'error' && (
                                    <span className={styles.statusError}>✗ Failed</span>
                                )}
                            </div>
                        )}

                        {/* Calendar Selection */}
                        <div className={styles.calendarSelection}>
                            <h5>Calendars</h5>
                            <p className={styles.helpText}>
                                Toggle calendars on or off to control which events are synced. All calendars are enabled by default.
                            </p>

                            {isFetchingCalendars ? (
                                <div className={styles.loadingState}>Loading calendars...</div>
                            ) : availableCalendars.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <p>No calendars found</p>
                                    <button onClick={fetchAvailableCalendars}>Refresh</button>
                                </div>
                            ) : (
                                <div className={styles.calendarList}>
                                    {availableCalendars.map(calendar => {
                                        const isEnabled = selectedCalendars.some(cal => cal?.id === calendar.id)
                                        return (
                                            <div key={calendar.id} className={styles.calendarItem}>
                                                <div className={styles.calendarDetails}>
                                                    <div
                                                        className={styles.calendarColor}
                                                        style={{ backgroundColor: calendar.background_color || '#3B82F6' }}
                                                    />
                                                    <div className={styles.calendarInfo}>
                                                        <div className={styles.calendarName}>
                                                            {calendar.summary}
                                                            {calendar.primary && (
                                                                <span className={styles.primaryBadge}>Primary</span>
                                                            )}
                                                        </div>
                                                        {calendar.description && (
                                                            <div className={styles.calendarDescription}>
                                                                {calendar.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <label className={styles.toggleSwitch}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isEnabled}
                                                        onChange={() => handleToggleCalendar(calendar.id)}
                                                    />
                                                    <span className={styles.toggleSlider}></span>
                                                </label>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            {availableCalendars.length > 0 && (
                                <div className={styles.calendarActions}>
                                </div>
                            )}
                        </div>

                        {/* Recurring Events Section */}
                        <div className={styles.recurringEventsSection}>
                            <div className={styles.recurringEventsToggleHeader}>
                                <div>
                                    <h5>Save recurring events on your calendar</h5>
                                    <p className={styles.helpText}>
                                        The recurring events on your Google calendar will be added to your timesheet calendar for the present week by default as an entry.
                                    </p>
                                </div>
                                <button
                                    className={styles.expandIconButton}
                                    onClick={() => handleToggleRecurringEventsSection(!showRecurringEventsSection)}
                                    aria-expanded={showRecurringEventsSection}
                                >
                                    <svg
                                        className={`${styles.expandIcon} ${showRecurringEventsSection ? styles.expanded : ''}`}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {showRecurringEventsSection && (
                                <>
                                    {/* Inline Events List */}
                                    {isFetchingRecurringEvents ? (
                                        <div className={styles.loadingState}>Loading recurring events...</div>
                                    ) : eventsResponse.all_recurring_events && eventsResponse.all_recurring_events.length > 0 ? (
                                        <div className={styles.eventsListInline}>
                                            {eventsResponse.all_recurring_events.map(event => {
                                                const existingPref = configuredPreferences.find(
                                                    p => p.recurring_event_id === event.recurring_event_id
                                                )

                                                return (
                                                    <div key={event.recurring_event_id} className={styles.eventItem}>
                                                        <div className={styles.eventInfo}>
                                                            <div className={styles.eventTitle}>
                                                                {event.summary}
                                                                {eventSelections[event.recurring_event_id]?.enabled && (
                                                                    <span className={styles.frequencyIndicator}>
                                                                        (updating {eventSelections[event.recurring_event_id]?.frequency || 'daily'})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {event.description && (
                                                                <div className={styles.eventDescription}>{event.description}</div>
                                                            )}
                                                        </div>

                                                        <div className={styles.eventSelectors}>
                                                            <select
                                                                value={getProjectForEvent(event.recurring_event_id)}
                                                                onChange={(e) => handleProjectChangeForEvent(event.recurring_event_id, e.target.value)}
                                                                className={styles.projectSelect}
                                                            >
                                                                <option value="">Select Project</option>
                                                                {projects.map(project => (
                                                                    <option key={project.id} value={project.id}>
                                                                        {project.name}
                                                                    </option>
                                                                ))}
                                                            </select>

                                                            <select
                                                                value={eventSelections[event.recurring_event_id]?.taskId || ''}
                                                                onChange={(e) => handleTaskChangeForEvent(event.recurring_event_id, e.target.value)}
                                                                className={styles.taskSelect}
                                                            >
                                                                <option value="">Select Task (Optional)</option>
                                                                {getTasksForEvent(event.recurring_event_id).map(task => (
                                                                    <option key={task.id} value={task.id}>
                                                                        {task.name}
                                                                    </option>
                                                                ))}
                                                            </select>

                                                            <div className={styles.eventToggleContainer}>
                                                                <span className={styles.eventToggleLabel}>Enable Daily Updates</span>
                                                                <label className={styles.toggleSwitch}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={eventSelections[event.recurring_event_id]?.enabled || false}
                                                                        onChange={(e) => handleToggleEventEnabled(event.recurring_event_id, e.target.checked)}
                                                                    />
                                                                    <span className={styles.toggleSlider}></span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <p>No recurring events found</p>
                                            <p className={styles.emptyStateSubtext}>
                                                Create recurring events in Google Calendar first, then sync your calendars above.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
                ) : (
                    <div className={styles.integrationCard}>
                        <div className={styles.integrationHeader}>
                            <div className={styles.integrationInfo}>
                                <div className={styles.integrationIcon}>
                                    <img
                                        src="/google-calendar-logo.png"
                                        alt="Google Calendar"
                                        width="48"
                                        height="48"
                                    />
                                </div>
                                <div>
                                    <h4>Google Calendar</h4>
                                    <p>Connect Kadlekai to your Google account to sync calendar events</p>
                                </div>
                            </div>
                            <span className={styles.proBadge}>PRO</span>
                        </div>
                        <div className={styles.upgradePrompt}>
                            <p>Upgrade to Pro to connect your Google Calendar and automatically sync events to your timesheet.</p>
                            <button
                                className={styles.upgradeButton}
                                onClick={() => setActiveSection('billing')}
                            >
                                Upgrade to Pro
                            </button>
                        </div>
                    </div>
                )}

        </div>
        )
    }

    const renderContent = () => {
        switch (activeSection) {
            case 'appearance':
                return renderAppearanceSection()
            case 'account':
                return renderAccountSection()
            case 'integrations':
                return renderIntegrationsSection()
            case 'tokens':
                return renderPersonalAccessTokensSection()
            default:
                return renderAppearanceSection()
        }
    }

    return (
        <div className={styles.preferencesOverlay}>
            <div className={styles.preferencesContainer}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h1>Preferences</h1>
                        <p>Customize your application settings and preferences</p>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close preferences"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.content}>
                    {/* Sidebar Navigation */}
                    <div className={styles.sidebar}>
                        <nav className={styles.sidebarNav}>
                            <button
                                className={`${styles.sidebarItem} ${activeSection === 'appearance' ? styles.active : ''}`}
                                onClick={() => setActiveSection('appearance')}
                            >
                                <span className={styles.sidebarIcon}>🎨</span>
                                Appearance
                            </button>
                            <button
                                className={`${styles.sidebarItem} ${activeSection === 'account' ? styles.active : ''}`}
                                onClick={() => setActiveSection('account')}
                            >
                                <span className={styles.sidebarIcon}>👤</span>
                                Account
                            </button>
                            <button
                                className={`${styles.sidebarItem} ${activeSection === 'integrations' ? styles.active : ''}`}
                                onClick={() => setActiveSection('integrations')}
                            >
                                <span className={styles.sidebarIcon}>🔗</span>
                                Integrations
                            </button>
                            <button
                                className={`${styles.sidebarItem} ${activeSection === 'tokens' ? styles.active : ''}`}
                                onClick={() => setActiveSection('tokens')}
                            >
                                <span className={styles.sidebarIcon}>🔑</span>
                                Personal Access Tokens
                            </button>
                        </nav>
                    </div>

                    {/* Main Content */}
                    <div className={styles.mainContent}>
                        {renderContent()}
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button
                        className={styles.closeFooterButton}
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Upgrade Prompt Modal */}
            {showUpgradeModal && upgradeModalData && (
                <UpgradePromptModal
                    isOpen={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    featureName={upgradeModalData.feature || 'google_calendar_integration'}
                    currentPlan={upgradeModalData.current_plan || 'free'}
                    requiredPlan={upgradeModalData.required_plan || 'pro'}
                    upgradeInfo={upgradeModalData}
                    onUpgrade={() => {
                        setShowUpgradeModal(false)
                        setActiveSection('billing')
                    }}
                />
            )}
        </div>
    )
}